/**
 * Referral Code Persistent Storage
 *
 * Stores and retrieves referral codes captured from deep links / URL params.
 * Used by useReferralAttribution to persist codes across app restarts
 * until signup attribution is completed.
 *
 * Task: NocoDB #1552 — Referral attribution capture hook
 * Epic: #1529 — DD Affiliate + Customer Referral Program
 */

import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';

const REFERRAL_CODE_KEY = 'dd_referral_code';
const REFERRAL_TIMESTAMP_KEY = 'dd_referral_timestamp';
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Validates referral code format: alphanumeric + hyphens/underscores, 4-64 chars */
export const REFERRAL_CODE_PATTERN = /^[a-zA-Z0-9_-]{4,64}$/;

export function isValidReferralCode(code: string): boolean {
  return REFERRAL_CODE_PATTERN.test(code);
}

/**
 * Store a referral code from a deep link or URL param.
 * Overwrites any existing code (most recent referral wins).
 */
export async function storeReferralCode(code: string): Promise<void> {
  if (Platform.OS === 'web') {
    try {
      localStorage.setItem(REFERRAL_CODE_KEY, code);
      localStorage.setItem(REFERRAL_TIMESTAMP_KEY, Date.now().toString());
    } catch {
      // localStorage not available (SSR, private browsing)
    }
  } else {
    await SecureStore.setItemAsync(REFERRAL_CODE_KEY, code);
    await SecureStore.setItemAsync(REFERRAL_TIMESTAMP_KEY, Date.now().toString());
  }
}

/**
 * Retrieve the stored referral code, or null if none / expired.
 */
export async function getReferralCode(): Promise<string | null> {
  let code: string | null = null;
  let timestamp: string | null = null;

  if (Platform.OS === 'web') {
    try {
      code = localStorage.getItem(REFERRAL_CODE_KEY);
      timestamp = localStorage.getItem(REFERRAL_TIMESTAMP_KEY);
    } catch {
      return null;
    }
  } else {
    code = await SecureStore.getItemAsync(REFERRAL_CODE_KEY);
    timestamp = await SecureStore.getItemAsync(REFERRAL_TIMESTAMP_KEY);
  }

  if (!code) return null;

  // Check TTL — expire after 30 days
  if (timestamp) {
    const storedAt = parseInt(timestamp, 10);
    if (Date.now() - storedAt > REFERRAL_TTL_MS) {
      await clearReferralCode();
      return null;
    }
  }

  return code;
}

/**
 * Clear the stored referral code (after successful attribution).
 */
export async function clearReferralCode(): Promise<void> {
  if (Platform.OS === 'web') {
    try {
      localStorage.removeItem(REFERRAL_CODE_KEY);
      localStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
    } catch {
      // Ignore
    }
  } else {
    await SecureStore.deleteItemAsync(REFERRAL_CODE_KEY);
    await SecureStore.deleteItemAsync(REFERRAL_TIMESTAMP_KEY);
  }
}
